<template>
  <div class="flex flex-shrink-0 flex-col px-5 py-3">
    <div class="flex justify-between">
      <div class="text-2xl font-bold capitalize-first">
        {{ $t('enums.invoicing.docTypes.' + invoice.doc_type) }}
      </div>
      <div v-if="!validated && invoice.doc_type === 'QUO'">
        <BaseDropDownMenu :options="menuOptions" :is-button="false" />
      </div>
    </div>
    <h2 class="-mt-1 text-sm font-medium">
      {{ funeral_home_name }}
    </h2>
    <div v-if="!validated" class="mt-2">
      <StatusInput
        v-model="invoice.doc_role"
        :options="doc_roles"
        :disabled="!$can('manage_invoicing')"
        @status-changed="changeRole"
      />
    </div>
    <span v-else :data-tint="docRole?.bg_color">
      <span class="rounded-md bg-primary px-2 text-contrast-primary">
        <span class="test-xs capitalize-first">{{ docRole?.name }}</span>
      </span>
    </span>
    <div v-if="invoice.invoice_items?.length" class="mt-2">
      <div
        v-if="invoice.status === 'CAN'"
        data-tint="faded"
        class="flex flex-row items-center gap-2"
        :class="`border-faded rounded border px-2`"
      >
        <span class="h-3 w-3 flex-shrink-0 rounded-sm bg-gray-500">&nbsp;</span>
        <span class="text-sm">{{ $t('enums.invoicing.status.CAN') }}</span>
      </div>
      <span v-else>
        <StatusInput
          v-if="validated"
          v-model="oldStatus"
          :options="statusOptions"
          @status-changed="changeStatus"
        />
        <div v-else>
          <BaseButton
            v-if="canValidate"
            :title="$t('global.validate')"
            theme-color="bg-secondary text-contrast-secondary"
            size="extra-small"
            @click="validateModalOpened = true"
          />
        </div>
      </span>
      <div class="mt-1">
        <StatusMultiInput
          v-model="invoice.tags"
          :options="invoice_status_tags"
          @status-changed="changeStatusTag"
        />
      </div>
    </div>
  </div>
  <div class="flex min-h-0 flex-1">
    <div
      class="scrollbar-custom flex flex-1 flex-col gap-3 overflow-auto px-5 pb-3"
    >
      <InvoicingShowInformation :invoice="invoice" />
    </div>
  </div>
  <ConfirmModal ref="warningChangeInvoiceStatusToDraft" />
  <ConfirmModal ref="confirmChangeDossierInvoiceStatus" />
  <ValidateModal v-model:modal-opened="validateModalOpened" />
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import StatusInput from '@c/addf-package/components/status/StatusInput.vue'
import BaseDropDownMenu from '@c/addf-package/components/dropdown/BaseDropDownMenu.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import InvoicingShowInformation from '@/views/invoicing/show/tabs/information/components/information/InvoicingShowInformation.vue'
import Status from '../../../../../../../assets/enums/invoicing/Status'
import DocTypes from '../../../../../../../assets/enums/invoicing/DocTypes'
import ValidateModal from '@/views/invoicing/show/tabs/information/components/information/components/ValidateModal.vue'
import ConfirmModal from '@c/addf-package/components/modal/ConfirmModal.vue'
import BaseShowEditInput from '@c/addf-package/components/BaseShowEditInput/BaseShowEditInput.vue'
import SelectComponent from '@c/BaseFormComponent/SelectComponent.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import StatusMultiInput from '@c/addf-package/components/status/StatusMultiInput.vue'
import { suggestedActionHandler } from '@/views/layouts/main-layout/ai-assistant/suggestedActionHandler'

export default {
  name: 'InvoicingLeftPanel',
  mixins: [suggestedActionHandler(['establish'])],
  components: {
    StatusMultiInput,
    FontAwesomeIcon,
    SelectComponent,
    BaseShowEditInput,
    ConfirmModal,
    ValidateModal,
    InvoicingShowInformation,
    BaseButton,
    BaseDropDownMenu,
    StatusInput
  },
  props: {
    invoice: {
      type: Object,
      required: true
    },
    hasBlockers: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      validateModalOpened: false,
      oldStatus: this.invoice?.status
    }
  },
  computed: {
    ...mapGetters({
      invoice_doc_types: 'metadata/getInvoiceDocTypes',
      invoice_status_tags: 'metadata/getMetadataInvoiceStatusTags',
      initialMetadata: 'metadata/getInitialMetadata',
      doc_roles: 'metadata/getInvoiceDocRoles'
    }),
    canValidate() {
      if (this.hasBlockers) return false

      const permissions = {
        [DocTypes.INVOICE]: 'validate_invoice',
        [DocTypes.INVOICE_PROFORMA]: 'validate_invoice',
        [DocTypes.CREDIT_NOTE]: 'validate_credit_note',
        [DocTypes.PURCHASE_ORDER]: 'validate_purchase_order',
        [DocTypes.QUOTE]: 'validate_quote'
      }

      const permission = permissions[this.invoice.doc_type]
      return this.$can(permission)
    },
    statusOptions() {
      let available_statuses =
        this.invoice_doc_types.find(
          (type) => type.id === this.invoice?.doc_type
        )?.available_statuses ?? []

      if (this.invoice.status !== Status.DRAFT) {
        available_statuses = available_statuses.filter(
          (item) => item.id !== Status.DRAFT
        )
      }

      if (
        this.invoice.status === Status.ESTABLISHED &&
        this.$can('reset_invoicing_to_draft')
      ) {
        const draftStatus = this.initialMetadata?.enums?.invoice_status.find(
          (item) => item.id === Status.DRAFT
        )
        if (draftStatus) {
          available_statuses.push(draftStatus)
        }
      }

      if (this.invoice.reference) {
        available_statuses = available_statuses.filter((item) => {
          if (
            this.invoice.doc_type === DocTypes.INVOICE &&
            item.id === Status.CANCELLED
          ) {
            return this.invoice.status === Status.CANCELLED
          }
          return true
        })
      }
      return available_statuses
    },
    validated() {
      return this.invoice.reference && this.invoice.reference.length
    },
    docRole() {
      return this.doc_roles.find((role) => role.id === this.invoice?.doc_role)
    },
    funeral_home_name() {
      return this.invoice?.funeral_home?.name
    },
    menuOptions() {
      return [
        [
          {
            label: this.$t('button.edit'),
            icon: 'pen',
            gate: true,
            action: () => this.$router.push({ name: 'invoice_edit_route' })
          }
        ]
      ]
    }
  },
  watch: {
    validateModalOpened(newValue) {
      if (newValue === false) {
        if (this?.invoicing_id !== undefined) {
          this.fetchInvoiceID({
            id: this?.invoicing_id,
            supplier_order_index: null
          })
        }
        this.oldStatus = this.invoice.status
      }
    }
  },
  methods: {
    // Suggested by the assistant on a draft left aside: same guards as the button
    handleSuggestedAction() {
      if (this.canValidate) this.validateModalOpened = true
    },
    ...mapActions({
      processChangeInvoiceRoleAction: 'invoicing/changeInvoiceRole',
      processChangeInvoiceStatusAction: 'invoicing/changeInvoiceStatus',
      processChangeInvoiceStatusTagAction: 'invoicing/changeInvoiceStatusTag',
      fetchInvoiceID: 'invoicing/fetchInvoiceID'
    }),
    async changeRole(newRole) {
      let form_data = {
        invoice_id: this.invoice.id,
        form_data: {
          role: newRole
        },
        successCallback: () =>
          this.$h.toastSuccess(this.$t('toasts.role_updated'))
      }

      this.processChangeInvoiceRoleAction(form_data)
    },
    async changeStatus(newStatus) {
      if (
        newStatus === Status.DRAFT &&
        this.invoice.status === Status.ESTABLISHED
      ) {
        let confirm = await this.$refs.warningChangeInvoiceStatusToDraft.show({
          title: this.$t('global.warning_reset_to_draft'),
          message: this.$t('global.warning_reset_invoice_to_draft'),
          okButton: this.$t('button.continue'),
          cancelButton: this.$t('button.cancel')
        })
        if (confirm) {
          await this.changeStatusAction(newStatus)
          this.oldStatus = newStatus
        }
      } else {
        await this.changeStatusAction(newStatus)
      }
    },
    async changeStatusTag(newStatus) {
      await this.changeStatusTagAction(newStatus)
    },
    async changeStatusAction(newStatus) {
      let form_data = {
        invoice_id: this.invoice.id,
        form_data: {
          status: newStatus
        },
        successCallback: () =>
          this.$h.toastSuccess(this.$t('toasts.status_updated'))
      }
      if (
        this.invoice.doc_role === 'ff' &&
        newStatus === 'PAI' &&
        this.invoice.dossier_id &&
        this.invoice.dossier?.status !== 'PAY'
      ) {
        let confirm = await this.$refs.confirmChangeDossierInvoiceStatus.show({
          title: this.$t('global.confirm_updating_dossier_status'),
          okButton: this.$t('global.yes'),
          cancelButton: this.$t('global.no')
        })
        if (confirm) {
          form_data.form_data.change_status_dossier = true
        }
      }
      this.processChangeInvoiceStatusAction(form_data)
    },
    async changeStatusTagAction(newStatus) {
      let form_data = {
        invoice_id: this.invoice.id,
        form_data: {
          status_tag: newStatus
        },
        successCallback: () =>
          this.$h.toastSuccess(this.$t('toasts.status_tag_updated'))
      }
      this.processChangeInvoiceStatusTagAction(form_data)
    }
  }
}
</script>
